import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, Col, Row, Typography, notification } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { AdditionalRequirementsSummaryCard } from './AdditionalRequirementsSummaryCard';

import SalesOrderStore from '../../stores/SalesOrderStore';
import ContactStore from '../../stores/ContactStore';
import PortalLoading from '../../components/PortalLoading';
import QuoteStore from '../../stores/QuoteStore';
import { PageHeader } from '../../components/PageHeader';
import { ErrorRender } from '@partsbadger/library';
import type { QuoteProps } from '@partsbadger/types';
import OrderItemsReview from './OrderItemsReview';
import OrderCheckoutSummary, { OrderCheckoutFormData } from './OrderCheckoutSummary';

import { buildSendToReviewPayloadFromOrderForm } from './orderCheckout';
import UIStore from '@/stores/UIStore';
import { trackLuckyOrangeEvent } from '@/utils/luckyOrange';
import { trackOrderCreated } from '@/utils/gtm';

const { Title } = Typography;

interface RouteParams {
    orderId: string;
}

type Props = { mode: 'portal' | 'rapid' };

type SubmitOverrides = {
    phone?: string;
    email?: string;
    shippingAccountNumber?: string;
    carrier?: string;
    shippingMethod?: string;
    saveShippingDefaults?: boolean;
};

const OrderReview: React.FC<Props> = observer(() => {
    const history = useHistory();
    const { orderId } = useParams<RouteParams>();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [quoteForReqs, setQuoteForReqs] = useState<QuoteProps | null>(null);

    const customReqs = QuoteStore?.quote?.custom_requirements ?? [];
    const appCostItems = QuoteStore?.quote?.application_cost_items ?? [];
    const hasAdditionalRequirements = customReqs.length > 0 || appCostItems.length > 0;

    console.log(QuoteStore?.quote);
    const [formData, setFormData] = useState<OrderCheckoutFormData>({
        shippingAddressId: undefined,
        billingAddressId: undefined,
        phone: '',
        email: '',
        billingSameAsShipping: true,
        paymentMethod: 'credit_card',
        purchaseOrderInfo: { poFiles: [] },
        saveShippingDefaults: false,
    });

    const repairOrderShippingOnLoad = async () => {
        const o: any = SalesOrderStore.order;
        const currentId = Number(o?.shipping_address_id || o?.shipping_address?.id || 0) || undefined;

        const addrExists = currentId ? ContactStore.shippingAddresses.some(a => a.id === currentId) : false;

        // Pick fallback default if missing/deleted
        const fallback = (() => {
            const def = ContactStore.default_shipping_address as any;
            if (def?.id && ContactStore.shippingAddresses.some(a => a.id === def.id)) return def.id;
            const primary = ContactStore.shippingAddresses.find(a => (a as any).primary);
            return primary?.id ?? ContactStore.shippingAddresses[0]?.id;
        })();

        const targetId = addrExists ? currentId : fallback;

        if (!targetId) return; // no addresses available; UI will force add

        // IMPORTANT: patch once even if unchanged to force tax refresh
        await handleShippingAddressChange(targetId);
    };

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                await SalesOrderStore.getOrderById(Number(orderId));

                const contactId = SalesOrderStore.order?.contact?.id;
                if (contactId) {
                    await ContactStore.getContactById(contactId);
                    await repairOrderShippingOnLoad();
                    await ContactStore.getAdditionalRequirementsAndDocuments(contactId);
                }

                const c = ContactStore.contact;
                const o: any = SalesOrderStore.order;

                setFormData(prev => {
                    const prevPhone = (prev.phone ?? '').trim();
                    const prevEmail = (prev.email ?? '').trim();

                    const seededPhone =
                        prevPhone ||
                        String(c?.phone ?? '').trim() ||
                        String(o?.contact?.phone ?? '').trim() ||
                        String(o?.phone ?? '').trim() ||
                        '';

                    const seededEmail =
                        prevEmail ||
                        String(c?.email ?? '').trim() ||
                        String(o?.contact?.email ?? '').trim() ||
                        String(o?.email ?? '').trim() ||
                        '';

                    return {
                        ...prev,
                        phone: seededPhone,
                        email: seededEmail,
                        shippingAccountNumber:
                            prev.shippingAccountNumber ||
                            (o?.shipping_account_number ?? '') ||
                            (c?.account?.shipping_account_number ?? ''),

                        carrier: prev.carrier || (o?.carrier ?? undefined) || (c?.account?.carrier ?? undefined),

                        shippingMethod:
                            prev.shippingMethod ||
                            (o?.shipping_method ?? undefined) ||
                            (c?.account?.shipping_method ?? undefined),
                    };
                });

                trackLuckyOrangeEvent('Checkout Started', {
                    order_id: orderId,
                    quote_id: QuoteStore.quote?.id ?? QuoteStore.getQuoteUid ?? undefined,
                    page: window.location.pathname,
                });
            } catch (err: any) {
                notification.error({
                    message: 'Error loading checkout',
                    description: <ErrorRender error={err} />,
                });
                history.replace('/my-account/orders/');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const onFormUpdate = <K extends keyof OrderCheckoutFormData>(key: K, value: OrderCheckoutFormData[K]) =>
        setFormData(prev => ({ ...prev, [key]: value }));

    const patchAndRefreshOrder = async (payload: any) => {
        const oid = Number(orderId);
        await SalesOrderStore.patchOrderDraft(oid, payload);
        await SalesOrderStore.getOrderById(oid);
    };

    const shippingUpdateSeq = useRef(0);

    const handleShippingAddressChange = async (addrId: number) => {
        const oid = Number(orderId);
        const seq = ++shippingUpdateSeq.current;

        const same = formData.billingSameAsShipping;

        setFormData(prev => ({
            ...prev,
            shippingAddressId: addrId,
            ...(same ? { billingAddressId: addrId } : null),
        }));

        try {
            setSubmitting(true);

            // LOOKUP THE FULL ADDRESS OBJECT
            const addr = ContactStore.shippingAddresses.find(a => a.id === addrId);

            // If address record is missing (deleted), fallback to whatever the order currently has,
            // OR force user to choose again (best is fallback-to-order-fields so tax can still compute).
            const o: any = SalesOrderStore.order;

            const shippingFallback = {
                shipping_name: o?.shipping_name ?? '',
                shipping_street: o?.shipping_street ?? '',
                shipping_city: o?.shipping_city ?? '',
                shipping_state: o?.shipping_state ?? '',
                shipping_zip_code: o?.shipping_zip_code ?? '',
                shipping_country: o?.shipping_country ?? '',
            };

            const patchPayload: any = {
                shipping_address_id: addrId,
                ...(addr
                    ? {
                          shipping_name: addr.name ?? '',
                          shipping_street: addr.street ?? '',
                          shipping_city: addr.city ?? '',
                          shipping_state: addr.state ?? '',
                          shipping_zip_code: addr.zip_code ?? '',
                          shipping_country: typeof addr.country === 'string' ? addr.country : addr.country?.code ?? '',
                      }
                    : shippingFallback),
            };

            if (same) {
                patchPayload.billing_address_id = addrId;

                if (addr) {
                    patchPayload.billing_name = addr.name ?? '';
                    patchPayload.billing_street = addr.street ?? '';
                    patchPayload.billing_city = addr.city ?? '';
                    patchPayload.billing_state = addr.state ?? '';
                    patchPayload.billing_zip_code = addr.zip_code ?? '';
                    patchPayload.billing_country =
                        typeof addr.country === 'string' ? addr.country : addr.country?.code ?? '';
                } else {
                    // If same-as-shipping and address missing, keep billing aligned with the same fallback fields
                    patchPayload.billing_name = patchPayload.shipping_name;
                    patchPayload.billing_street = patchPayload.shipping_street;
                    patchPayload.billing_city = patchPayload.shipping_city;
                    patchPayload.billing_state = patchPayload.shipping_state;
                    patchPayload.billing_zip_code = patchPayload.shipping_zip_code;
                    patchPayload.billing_country = patchPayload.shipping_country;
                }
            } else if (formData.billingAddressId) {
                patchPayload.billing_address_id = formData.billingAddressId;
            }

            await SalesOrderStore.patchOrderDraft(oid, patchPayload);
            if (seq !== shippingUpdateSeq.current) return;

            await SalesOrderStore.getOrderById(oid);
            if (seq !== shippingUpdateSeq.current) return;
        } catch (err: any) {
            notification.error({ message: 'Failed to update shipping', description: <ErrorRender error={err} /> });
        } finally {
            if (seq === shippingUpdateSeq.current) setSubmitting(false);
        }
    };

    const handleBillingSameAsShippingChange = async (same: boolean) => {
        onFormUpdate('billingSameAsShipping', same);

        if (same) {
            const shipId = formData.shippingAddressId ?? null;
            if (shipId) {
                onFormUpdate('billingAddressId', shipId);
                await patchAndRefreshOrder({ billing_address_id: shipId });
            }
            return;
        }
    };

    const handleBillingAddressChange = async (billingId: number) => {
        onFormUpdate('billingAddressId', billingId);
        await patchAndRefreshOrder({ billing_address_id: billingId });
    };

    const derivedQuoteId = useMemo(() => {
        const items: any[] = SalesOrderStore.order_items ?? [];

        const raw = items.find(x => x?.quote_has_product?.quote_related?.id != null)?.quote_has_product?.quote_related
            ?.id;

        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [SalesOrderStore.order_items]);
    console.log(derivedQuoteId);
    const linkedQuoteRef = useRef<{ orderId?: string; quoteId?: number }>({});

    useEffect(() => {
        const oidStr = String(orderId);
        const qid = derivedQuoteId;

        if (!oidStr || !qid) return;

        // If backend already linked it, bail.
        const orderQuotes = (SalesOrderStore.order as any)?.quotes;
        const alreadyLinked =
            Array.isArray(orderQuotes) && orderQuotes.some((x: any) => String(x?.id ?? x) === String(qid));

        if (alreadyLinked) {
            linkedQuoteRef.current = { orderId: oidStr, quoteId: qid };
            return;
        }

        (async () => {
            try {
                linkedQuoteRef.current = { orderId: oidStr, quoteId: qid };
            } catch (e) {
                // don't block checkout if quote-linking fails
                console.warn('Failed to link quote to order draft', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, derivedQuoteId]);

    // Fetch quote only after order items exist
    useEffect(() => {
        if (!derivedQuoteId) return;
        // QuoteStore.getQuoteById(Number(derivedQuoteId));

        QuoteStore.getLastDraftQuoteById(String(derivedQuoteId)).catch(err => {
            console.warn('Failed to load quote for order review', err);
        });
    }, [derivedQuoteId]);

    useEffect(() => {
        if (!derivedQuoteId) {
            setQuoteForReqs(null);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const q = await QuoteStore.getQuoteById(derivedQuoteId);
                if (!cancelled) setQuoteForReqs(q);
            } catch (e) {
                if (!cancelled) setQuoteForReqs(null);
                console.error('getQuoteById failed', e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [derivedQuoteId]);

    const goBackOrFallback = (fallbackUrl: string) => {
        const before = window.location.pathname + window.location.search + window.location.hash;

        history.goBack();

        // If back didn't change the URL (or there was nowhere to go), use fallback.
        window.setTimeout(() => {
            const after = window.location.pathname + window.location.search + window.location.hash;
            if (after === before) {
                history.push(fallbackUrl);
            }
        }, 150);
    };

    const handleEditQuote = () => {
        if (!derivedQuoteId) {
            notification.warning({
                message: 'Missing quote id',
                description: 'Can’t edit quote because this order is not linked to a quote.',
            });
            return;
        }

        const order_items = SalesOrderStore.order_items ?? [];
        const isADraftQuote =
            order_items.filter(item => item.quote_has_product?.quote_related?.stage === 'Draft').length > 0;

        const quoteId = order_items.find(item => item.quote_has_product?.quote_related?.stage === 'Draft')
            ?.quote_has_product?.quote_related?.id;

        if (isADraftQuote) {
            history.push(`/my-account/quotes/${quoteId}/continue/`);
        } else {
            const fallback = `/my-account/quotes/${derivedQuoteId}/order/`;
            goBackOrFallback(fallback);
        }
    };

    const ensureQuoteLinked = async () => {
        const qid = derivedQuoteId;
        if (!qid) return;

        const orderQuotes = (SalesOrderStore.order as any)?.quotes;
        const alreadyLinked =
            Array.isArray(orderQuotes) && orderQuotes.some((x: any) => String(x?.id ?? x) === String(qid));

        if (alreadyLinked) return;

        await SalesOrderStore.getOrderById(Number(orderId));
    };

    const handleSubmitPO = async (tokenOverride?: string, overrides?: SubmitOverrides) => {
        const oid = Number(orderId);

        setSubmitting(true);
        try {
            await ensureQuoteLinked();

            const phone = String(overrides?.phone ?? formData.phone ?? '').trim();
            const email = String(overrides?.email ?? formData.email ?? '').trim();

            const merged: OrderCheckoutFormData = {
                ...formData,
                ...overrides,
                phone,
                email,
            };

            const shipId = merged.shippingAddressId;
            const same = !!merged.billingSameAsShipping;

            merged.billingAddressId = same ? shipId : merged.billingAddressId;

            await SalesOrderStore.patchOrderDraft(oid, {
                phone,
                email,
                shipping_address_id: merged.shippingAddressId ?? null,
                billing_same_as_shipping: !!merged.billingSameAsShipping,
                billing_address_id:
                    (merged.billingSameAsShipping ? merged.shippingAddressId : merged.billingAddressId) ?? null,
                shipping_account_number: merged.shippingAccountNumber ?? '',
                carrier: merged.carrier ?? null,
                shipping_method: merged.shippingMethod ?? null,
                save_shipping_defaults: !!merged.saveShippingDefaults,
            });

            const payload = buildSendToReviewPayloadFromOrderForm(merged, {
                credit_card_token: tokenOverride || 'PURCHASE_ORDER',
                source: 'Order Checkout - Customer',
            });

            await SalesOrderStore.sendToReview(oid, {
                ...payload,
                additional_requirements: customReqs?.map((r: any) => {
                    return r.value ? `${r.label} : ${r.value}` : r.label;
                }),
            })
                .then(async () => {
                    trackOrderCreated({
                        order_id: oid,
                        payment_method: payload.pay_with_credit_card ? 'credit_card' : 'purchase_order',
                        source: 'portal_checkout',
                    });
                    await QuoteStore.cleanQuote();
                    notification.success({
                        message: 'Order submitted',
                        description: 'Your order was submitted for review.',
                    });
                    history.push('/my-account/orders/');
                })
                .catch((err: any) => {
                    trackLuckyOrangeEvent('Payment Failed', {
                        order_id: orderId,
                        stage: 'order_submission',
                        page: window.location.pathname,
                    });
                    notification.error({
                        message: 'Order submission failed',
                        description: <ErrorRender error={err} />,
                    });
                });
        } catch (err: any) {
            trackLuckyOrangeEvent('Payment Failed', {
                order_id: orderId,
                stage: 'checkout',
                page: window.location.pathname,
            });
            notification.error({ message: 'Checkout failed', description: <ErrorRender error={err} /> });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCardToken = async (token: string, overrides?: SubmitOverrides) => {
        const oid = Number(orderId);

        if (!token) {
            notification.error({ message: 'Payment failed', description: 'Missing payment token. Please try again.' });
            return;
        }

        setSubmitting(true);
        try {
            await ensureQuoteLinked();

            const phone = String(overrides?.phone ?? formData.phone ?? '').trim();
            const email = String(overrides?.email ?? formData.email ?? '').trim();

            const merged: OrderCheckoutFormData = {
                ...formData,
                ...overrides,
                phone,
                email,
            };

            const shipId = merged.shippingAddressId;
            const same = !!merged.billingSameAsShipping;

            merged.billingAddressId = same ? shipId : merged.billingAddressId;

            await SalesOrderStore.patchOrderDraft(oid, {
                phone,
                email,
                shipping_address_id: merged.shippingAddressId ?? null,
                billing_same_as_shipping: !!merged.billingSameAsShipping,
                billing_address_id:
                    (merged.billingSameAsShipping ? merged.shippingAddressId : merged.billingAddressId) ?? null,
            });

            const payload = buildSendToReviewPayloadFromOrderForm(merged, {
                credit_card_token: token,
                source: 'Order Checkout - Customer',
            });

            await SalesOrderStore.sendToReview(oid, {
                ...payload,
                additional_requirements: customReqs?.map((r: any) => {
                    return r.value ? `${r.label} : ${r.value}` : r.label;
                }),
            })
                .then(async () => {
                    trackOrderCreated({
                        order_id: oid,
                        payment_method: payload.pay_with_credit_card ? 'credit_card' : 'purchase_order',
                        source: 'portal_checkout',
                    });
                    await QuoteStore.cleanQuote();
                    notification.success({ message: 'Payment processed', description: 'Order placed successfully.' });
                    history.push('/my-account/orders/');
                })
                .catch((err: any) => {
                    trackLuckyOrangeEvent('Payment Failed', {
                        order_id: orderId,
                        stage: 'payment_submission',
                        page: window.location.pathname,
                    });
                    notification.error({
                        message: 'Payment submission failed',
                        description: <ErrorRender error={err} />,
                    });
                });
        } catch (err: any) {
            trackLuckyOrangeEvent('Payment Failed', {
                order_id: orderId,
                stage: 'payment',
                page: window.location.pathname,
            });
            notification.error({ message: 'Payment failed', description: <ErrorRender error={err} /> });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequote = async () => {
        const qid = quoteForReqs?.id ?? derivedQuoteId;
        if (!qid) {
            notification.error({
                message: 'Missing quote id',
                description: 'We can’t request a requote because we couldn’t determine the quote id.',
            });
            return;
        }

        try {
            const res = await QuoteStore.requote(qid);

            history.replace(`/my-account/quotes/${res.id}/continue/`);
        } catch (e) {
            notification.error({
                message: 'Requote failed',
                description: 'Something went wrong requesting a requote. Please try again.',
            });
        }
    };

    if (loading || !SalesOrderStore.order || !ContactStore.contact) return <PortalLoading />;

    const order_items = SalesOrderStore.order_items ?? [];
    const isADraftQuote =
        order_items.filter(item => item.quote_has_product?.quote_related?.stage === 'Draft').length > 0;

    const quoteId = order_items.find(item => item.quote_has_product?.quote_related?.stage === 'Draft')
        ?.quote_has_product?.quote_related?.id;

    const mode = isADraftQuote ? 'rapid_quote' : 'quote_detail';

    return (
        <>
            <PageHeader>
                <div className="d-flex flex-column align-items-baseline">
                    <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            if (isADraftQuote) {
                                history.push(`/my-account/quotes/${quoteId}/continue/`);
                            } else {
                                history.goBack();
                            }
                        }}
                        className="text-primary m-0 p-0"
                    >
                        Back
                    </Button>

                    <Title level={1} className="mb-0">
                        Checkout
                    </Title>
                </div>
            </PageHeader>

            <div
                className="container"
                data-testid="customer-order-checkout-page"
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Row gutter={[16, 16]} style={{ flex: 1, minHeight: 0 }}>
                    <Col
                        xl={16}
                        lg={16}
                        md={24}
                        span={24}
                        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
                    >
                        <div
                            style={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: UIStore.isConstrainedViewport ? 'visible' : 'auto',
                            }}
                        >
                            <OrderItemsReview
                                order={SalesOrderStore.order}
                                order_items={SalesOrderStore.order_items}
                                contact={ContactStore.contact}
                                onEditOrder={handleEditQuote}
                                quote={quoteForReqs ?? undefined}
                            />

                            {hasAdditionalRequirements && (
                                <Card className="mt-3">
                                    <AdditionalRequirementsSummaryCard
                                        quote={QuoteStore?.quote}
                                        mode={mode}
                                        onEdit={mode === 'rapid_quote' ? handleEditQuote : undefined}
                                        onRequote={mode !== 'rapid_quote' ? handleRequote : undefined}
                                    />
                                </Card>
                            )}
                        </div>
                    </Col>

                    <Col xl={8} lg={8} md={24} span={24}>
                        <OrderCheckoutSummary
                            order={SalesOrderStore.order}
                            contact={ContactStore.contact}
                            order_items={SalesOrderStore.order_items}
                            formData={formData}
                            submitting={submitting}
                            onFormUpdate={onFormUpdate}
                            onSubmitPO={handleSubmitPO}
                            onCreditCardToken={handleCardToken}
                            onShippingAddressChange={handleShippingAddressChange}
                            onBillingAddressChange={handleBillingAddressChange}
                            onBillingSameAsShippingChange={handleBillingSameAsShippingChange}
                        />
                    </Col>
                </Row>
            </div>
        </>
    );
});

export default OrderReview;
